/**
 * Onboarding step 1: the visitor's lab files upload one by one, each with its own progress and
 * its own error, and as soon as one is received they are asked for an email (ONB-010 to ONB-014).
 * The session starts here, anonymously, behind Turnstile (AUT-001), so the files belong to someone
 * before any personal detail is asked.
 *
 * Files upload in the order chosen, one at a time, so a slow phone connection gives each file the
 * whole connection and the progress bars mean something.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState } from "react";
import { isPdf } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import { Button } from "@lattice/ui/Button";
import { Skeleton } from "@lattice/ui/Skeleton";
import { visuallyHidden } from "@lattice/ui/visuallyHidden";
import { Status } from "@lattice/ui/Status";
import { Findings } from "./Findings.tsx";
import type { StatusState } from "@lattice/ui/Status";
import type { DocumentProgress, ProcessingProgress } from "@lattice/core";
import type { UploadOutcome } from "../api.ts";
import { captureEmail, loadCurrentFormula, loadIntake, loadProgress, signedIn, startSession, uploadFile } from "../api.ts";
import { intakeComplete } from "./questions.ts";
import { EmailLinkForm } from "../EmailLinkForm.tsx";
import { navigate } from "../router.ts";
import { turnstileToken } from "../turnstile.ts";
import type { Picked } from "../Upload.tsx";
import { useUploads } from "../Upload.tsx";
import { PouchShot } from "../PouchShot.tsx";
import { Shell, stepText } from "./Shell.tsx";
import { WhatHappens } from "./WhatHappens.tsx";
const FINE = "@media (pointer: fine)";
const PHONE = "@media (max-width: 599px)";
const WIDE = "@media (min-width: 960px)";
const arrive = "xrngkwq-B";
const _temp = {
  kzqmXN: "x5lhr3w",
  "$$css": true
};
const styles = {
  fill: {
    kZKoxP: "x5yr21d",
    kWkggS: "xovxxy8",
    k1ekBW: "xxrbq2n",
    kIyJzY: "x149xavy",
    kAMwcw: "xa02eh3",
    $$css: true
  },
  width: (fraction: number) => [_temp, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${Math.round(fraction * 100)}%`)
  }],
  phoneOnly: {
    k1xSpc: "x1lliihq x1p55870",
    $$css: true
  }
};
type FileState = {
  readonly kind: "waiting";
} | {
  readonly kind: "uploading";
  readonly fraction: number;
} | {
  readonly kind: "done";
  readonly outcome: UploadOutcome;
};
function problem(file: File, reason: "too_large" | "unsupported_type" | "failed"): string {
  if (reason === "failed") return "This upload didn't finish. Check your connection and try again.";
  if (reason === "unsupported_type") {
    return "We read PDF, JPEG, PNG and HEIC files. Save this one as a PDF and add it again.";
  }
  return isPdf(file.name, file.type) ? "This PDF is over 25 MB. Save only the pages with your results, or photograph them." : "This photo is over 25 MB. Take it again at a smaller size.";
}
function accepted(state: FileState | undefined): boolean {
  return state?.kind === "done" && state.outcome.status !== "rejected";
}
function documentFor(state: FileState | undefined, progress: ProcessingProgress | null): DocumentProgress | undefined {
  if (state?.kind !== "done") return undefined;
  const outcome = state.outcome;
  if (outcome.status === "rejected") return undefined;
  return progress?.documents.find(document => document.documentId === outcome.documentId);
}

/** A session for this visit, started once, anonymously, the first time a file needs one. */
async function ensureSession(): Promise<boolean> {
  if (await signedIn()) return true;
  return startSession(await turnstileToken());
}
function Check() {
  return <svg className="x1p2vmyc x2lah0s x1n2onr6 xs7f9wi" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>;
}
interface FileLabel {
  readonly text: string;
  readonly tone: "plain" | "ok" | "problem";
}
function fileLabel(state: FileState | undefined, document: DocumentProgress | undefined): FileLabel {
  if (state?.kind === "uploading") return {
    text: `${String(Math.round(state.fraction * 100))}%`,
    tone: "plain"
  };
  if (state?.kind !== "done") return {
    text: "Queued",
    tone: "plain"
  };
  if (state.outcome.status === "rejected") return {
    text: "Not added",
    tone: "problem"
  };
  if (document?.status === "failed") return {
    text: "Failed",
    tone: "problem"
  };
  if (document?.status === "stored") return {
    text: "Queued",
    tone: "plain"
  };
  if (document?.status === "extracting") return {
    text: "Reading",
    tone: "plain"
  };
  if (document?.status === "extracted") return {
    text: `Found ${String(document.markersFound)}`,
    tone: "ok"
  };
  return {
    text: state.outcome.status === "duplicate" ? "Already added" : "Received",
    tone: "ok"
  };
}
function FileStatus({
  state,
  document
}: {
  readonly state: FileState | undefined;
  readonly document: DocumentProgress | undefined;
}) {
  const label = fileLabel(state, document);
  return <span {...{
    0: {
      className: "x78zum5 x6s0dn4 xskm3c9 xl4dl4e x1jty25b xuxw1ft x1l1vl4p"
    },
    2: {
      className: "x78zum5 x6s0dn4 xskm3c9 xl4dl4e xuxw1ft x1l1vl4p x1p2vmyc xqu410z"
    },
    1: {
      className: "x78zum5 x6s0dn4 xskm3c9 xl4dl4e xuxw1ft x1l1vl4p x156x1qg"
    },
    3: {
      className: "x78zum5 x6s0dn4 xskm3c9 xl4dl4e xuxw1ft x1l1vl4p xqu410z x156x1qg"
    }
  }[!!(label.tone === "ok") << 1 | !!(label.tone === "problem") << 0]}>
      {label.tone === "ok" && <Check />}
      {label.text}
    </span>;
}
function FileRow({
  entry,
  state,
  document,
  onRetry,
  onRemove
}: {
  readonly entry: Picked;
  readonly state: FileState | undefined;
  readonly document: DocumentProgress | undefined;
  readonly onRetry: () => void;
  readonly onRemove: () => void;
}) {
  const refused = state?.kind === "done" && state.outcome.status === "rejected" ? state.outcome.reason : null;
  const extractFailed = document?.status === "failed" ? document.failureReason : null;
  const detail = refused ? problem(entry.file, refused) : extractFailed;
  return <li className="x78zum5 xdt5ytf x1173whf x1wiri7k x1t74ka8 x1q0q8m5 xso031l" data-testid="upload-row">
      <div className="x78zum5 x1qughib x1pha0wt x1r0x45p">
        <span className="xqu410z xb3r6kr xlyipyv xuxw1ft xeuugli">{entry.file.name}</span>
        <FileStatus state={state} document={document} />
      </div>
      {state?.kind === "uploading" && <div className="xqu0tyb x1m3fsnl x1890um5 xb3r6kr" role="progressbar" aria-label={`Uploading ${entry.file.name}`} aria-valuenow={Math.round(state.fraction * 100)}>
          <div {...stylex.props(styles.fill, styles.width(state.fraction))} />
        </div>}
      {detail && <>
          <p role="alert" className="x1ghz6dp xl4dl4e x156x1qg x1fzhlzt">
            {detail}
          </p>
          <div className="x78zum5 x1173whf">
            {refused === "failed" && <Button tone="quiet" size="small" onClick={onRetry}>
                Try again
              </Button>}
            <Button tone="quiet" size="small" onClick={onRemove}>
              Remove
            </Button>
          </div>
        </>}
    </li>;
}

/**
 * Where the visit stands once an email is in. "unknown" holds until the first progress answer.
 * "none" means the server holds no lab report for this session and nothing is uploading, so
 * waiting would never end and the visitor is asked for one.
 */
type LabsReadiness = "unknown" | "none" | "reading" | "ready";
interface Progress {
  readonly labs: LabsReadiness;
  readonly answersComplete: boolean;
  readonly inReview: boolean;
}
function NextSteps({
  labs,
  answersComplete,
  inReview
}: Progress) {
  if (labs === "none") return null;
  if (labs === "unknown") return <Skeleton bone="control" />;
  if (labs === "reading") return <Status>Waiting until your labs are ready</Status>;
  if (answersComplete && inReview) return <div {...stylex.props(stepText.actions)}>
        <Button testId="see-formula" wide onClick={() => navigate("/start/reviewing")}>
          See your formula
        </Button>
        <Button tone="quiet" wide onClick={() => navigate("/start/questions")}>
          Review your answers
        </Button>
      </div>;
  if (answersComplete) return <div {...stylex.props(stepText.actions)}>
        <Button testId="to-physician" wide onClick={() => navigate("/start/reviewing")}>
          Send to a physician
        </Button>
        <Button tone="quiet" wide onClick={() => navigate("/start/questions")}>
          Review your answers
        </Button>
      </div>;
  return <Button testId="to-questions" wide onClick={() => navigate("/start/questions")}>
      Next, a few health questions
    </Button>;
}
function SentNote({
  email,
  changed,
  labs
}: {
  readonly email: string;
  readonly changed: boolean;
  readonly labs: LabsReadiness;
}) {
  /* With no labs the panel's lead already says what to do, and the note points at it instead. */
  const after = labs === "ready" || labs === "reading" ? " You don't need to open it to keep going." : "";
  return <span className="xeuugli x1ghz6dp x78zum5 xdt5ytf">
      <span>{changed ? "We sent a new link to " : "We sent a link to "}</span>
      <span className="xqu410z xj0a0fe">{email}</span>
      {after === "" ? null : <span>{after}</span>}
    </span>;
}

/** Sends the same link to the same address again, for an email that never arrived. */
/** How long the button rests after a send, so a second press is not a second email by accident. */
const RESEND_REST_MS = 10_000;

/** Sends the same link to the same address again, for an email that never arrived. */
function ResendLink({
  email
}: {
  readonly email: string;
}) {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [resting, setResting] = useState(false);
  const send = async () => {
    setState("sending");
    const outcome = await turnstileToken().then(token => captureEmail(email, token)).catch(() => "failed" as const);
    setState(outcome === "sent" ? "sent" : "failed");
    if (outcome === "sent") {
      setResting(true);
      window.setTimeout(() => setResting(false), RESEND_REST_MS);
    }
  };
  return <div className="x78zum5 xdt5ytf x1173whf x1cy8zhl">
      <div>
        <Button tone="quiet" size="md" testId="resend-link" busy={state === "sending" || resting} onClick={() => void send()}>
          Send the link again
        </Button>
      </div>
      <div className="x175hb3s x1cuo5qr">
        {state === "sent" ? <p role="status" className="xeuugli x1ghz6dp x78zum5 xdt5ytf">
            Sent again. Check your spam folder.
          </p> : null}
        {state === "failed" ? <p role="alert" className="x1ghz6dp xl4dl4e x156x1qg x1fzhlzt">
            We couldn't send it again. Check your connection and try again.
          </p> : null}
      </div>
    </div>;
}
function Sent({
  email,
  changed,
  onChange,
  progress
}: {
  readonly email: string;
  /** True once the visitor has replaced the address they first gave. */
  readonly changed: boolean;
  readonly onChange: () => void;
  readonly progress: Progress;
}) {
  const note = useRef<HTMLParagraphElement>(null);
  const noLabs = progress.labs === "none";
  useEffect(() => note.current?.focus(), []);
  return <div className="x78zum5 xdt5ytf x15agyyk x1qjc9v5">
      <SentHeading labs={progress.labs} />
      <p ref={note} tabIndex={-1} role="status" aria-describedby={noLabs ? "no-labs-lead" : undefined} className="x1ghz6dp x78zum5 x1173whf x1pha0wt xzb3lse x1fzhlzt x1t137rt">
        <Check />
        <SentNote email={email} changed={changed} labs={progress.labs} />
      </p>
      <NextSteps {...progress} />
      <ResendLink email={email} />
      <div>
        <Button tone="quiet" size="md" onClick={onChange}>
          Change your email
        </Button>
      </div>
    </div>;
}

/*
  Until the first progress answer the panel cannot tell a visitor with labs from one without, so it
  holds the heading's place rather than promising a physician and taking it back.
*/
function SentHeading({
  labs
}: {
  readonly labs: LabsReadiness;
}) {
  if (labs === "unknown") return <div {...stylex.props(stepText.block)}>
        <h2 id="email-heading" {...stylex.props(stepText.subheading)}>
          Your email is in
        </h2>
        <Skeleton bone="line" />
      </div>;
  if (labs === "none") return <div {...stylex.props(stepText.block)}>
        <h2 id="email-heading" {...stylex.props(stepText.subheading)}>
          We still need your labs
        </h2>
        <p id="no-labs-lead" {...stylex.props(stepText.lead)} data-testid="no-labs">
          Upload them above and a physician can start on your formula.
        </p>
      </div>;
  return <div {...stylex.props(stepText.block)}>
      <h2 id="email-heading" {...stylex.props(stepText.subheading)}>
        Your email is in
      </h2>
      <p {...stylex.props(stepText.lead)}>
        A physician reviews your formula themselves, within 24 hours. Nothing is charged until they
        sign.
      </p>
    </div>;
}
function changeLead({
  labs,
  answersComplete
}: Progress): string {
  const send = "We'll send a new link to this address.";
  if (labs === "none" || labs === "unknown") return send;
  return answersComplete ? `${send} Your labs and answers stay saved.` : `${send} Your labs stay saved.`;
}

/**
 * The email step and, once an address is in, the panel that leads on to the questions.
 *
 * Changing the address keeps the same anonymous session and sends a new link to the new address.
 * The labs and answers already belong to that session, so nothing has to be uploaded again, and
 * later emails go to the newest address asked for. Signing out and starting a fresh session here
 * would leave the uploaded files on an account nobody can reach and the screen waiting on labs
 * that never arrive.
 */
function EmailSection({
  progress
}: {
  readonly progress: Progress;
}) {
  const [sentTo, setSentTo] = useState<string | null>(() => sessionStorage.getItem("lattice-email"));
  const [changing, setChanging] = useState(false);
  const [changed, setChanged] = useState(false);
  const section = useRef<HTMLElement>(null);
  useEffect(() => {
    const element = section.current;
    if (!element) return;
    const below = element.getBoundingClientRect().top > window.innerHeight - 120;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (below) element.scrollIntoView({
      behavior: still ? "auto" : "smooth",
      block: "start"
    });
  }, []);
  const showSent = sentTo !== null && !changing;

  /*
    The form sits on the page background, not the tinted panel. The form's 13px error red measures
    about 4.0:1 on the tint, under the 4.5:1 small text needs.
  */
  return <section ref={section} {...{
    0: {
      className: "x78zum5 xdt5ytf x1r0x45p x18kjutu xwlyjvd x1uhw61z x13fuv20 xamhcws x1yji3t xv8cai x1ah4f44 xcdydla x1ip1z55"
    },
    1: {
      className: "x78zum5 xdt5ytf x15agyyk x197fguk xq24xx7 x1hegsvj x1890um5 x1yji3t xv8cai x1ah4f44 xcdydla x1ip1z55"
    }
  }[!!showSent << 0]} aria-labelledby="email-heading">
      {showSent ? <Sent email={sentTo} changed={changed} progress={progress} onChange={() => setChanging(true)} /> : <>
          <div {...stylex.props(stepText.block)}>
            <h2 id="email-heading" {...stylex.props(stepText.subheading)}>
              {changing ? "Change your email" : "Where should we email you?"}
            </h2>
            <p id="email-lead" {...stylex.props(stepText.lead)}>
              {changing ? changeLead(progress) : "We email you when your physician has signed your formula. No password needed."}
            </p>
            {!changing && <p {...stylex.props(stepText.lead, styles.phoneOnly)}>
                $175 a month, charged only after your physician approves your formula.
              </p>}
          </div>
          <EmailLinkForm submitLabel={changing ? "Send new link" : "Continue"} initialEmail={sentTo ?? ""} focusOnMount={changing} {...changing && sentTo !== null ? {
        alreadySentTo: sentTo,
        describedBy: "email-lead"
      } : {}} onSent={email => {
        setChanged(sentTo !== null);
        sessionStorage.setItem("lattice-email", email);
        setSentTo(email);
        setChanging(false);
      }} request={captureEmail} />
          {changing && <div>
              <Button tone="quiet" size="small" onClick={() => setChanging(false)}>
                Keep my current email
              </Button>
            </div>}
        </>}
    </section>;
}
function useUploadQueue(picked: readonly Picked[]) {
  const [states, setStates] = useState<ReadonlyMap<string, FileState>>(new Map());
  const [sessionFailed, setSessionFailed] = useState(false);
  const running = useRef(false);
  const queued = useRef<Picked[]>([]);
  const seen = useRef(new Set<string>());
  const set = (id: string, state: FileState) => setStates(before => new Map(before).set(id, state));
  const drain = async () => {
    if (running.current) return;
    running.current = true;
    if (!(await ensureSession().catch(() => false))) {
      setSessionFailed(true);

      /* Each waiting file fails like one cut off mid-upload, so every row says so and offers Try again. */
      for (const entry of queued.current.splice(0)) set(entry.id, {
        kind: "done",
        outcome: {
          status: "rejected",
          reason: "failed"
        }
      });
      running.current = false;
      return;
    }
    setSessionFailed(false);
    for (let next = queued.current.shift(); next; next = queued.current.shift()) {
      const entry = next;
      set(entry.id, {
        kind: "uploading",
        fraction: 0
      });
      const outcome = await uploadFile(entry.file, fraction => set(entry.id, {
        kind: "uploading",
        fraction
      }));
      set(entry.id, {
        kind: "done",
        outcome
      });
    }
    running.current = false;
  };
  const enqueue = (entries: readonly Picked[]) => {
    for (const entry of entries) {
      set(entry.id, {
        kind: "waiting"
      });
      queued.current.push(entry);
    }
    void drain();
  };
  useEffect(() => {
    const fresh = picked.filter(entry => !seen.current.has(entry.id));
    for (const entry of fresh) seen.current.add(entry.id);
    if (fresh.length > 0) enqueue(fresh);
  }, [picked]);
  return {
    states,
    sessionFailed,
    retry: (entry: Picked) => enqueue([entry])
  };
}

/** How many answers in a row can fail before the page says it has lost touch. */
const LOST_AFTER = 3;
interface LabReading {
  readonly progress: ProcessingProgress | null;
  /** The last few answers all failed, so what the page shows may be out of date. */
  readonly lost: boolean;
}
function useLabReading(ready: boolean): LabReading {
  const [progress, setProgress] = useState<ProcessingProgress | null>(null);
  const [lost, setLost] = useState(false);
  useEffect(() => {
    if (!ready) return;
    let gone = false;
    let failures = 0;
    const tick = async () => {
      const next = await loadProgress().catch(() => null);
      if (gone) return;
      if (next) {
        failures = 0;
        setLost(false);
        setProgress(next);
      } else {
        failures += 1;
        if (failures >= LOST_AFTER) setLost(true);
      }
    };
    void tick();
    const id = setInterval(() => void tick(), 800);
    return () => {
      gone = true;
      clearInterval(id);
    };
  }, [ready]);
  return {
    progress,
    lost
  };
}
function readingSettled(progress: ProcessingProgress | null): boolean {
  if (!progress || progress.documents.length === 0) return false;
  return progress.documents.every(document => document.status === "extracted" || document.status === "failed");
}

/* Reports that could not be read give the physician nothing, so they are not labs that are ready. */
function nothingReadable(progress: ProcessingProgress | null): boolean {
  return progress !== null && progress.documents.length > 0 && progress.documents.every(document => document.status === "failed");
}
function labsReadiness(picked: readonly Picked[], states: ReadonlyMap<string, FileState>, progress: ProcessingProgress | null): LabsReadiness {
  if (nothingReadable(progress)) return "none";
  if (readingSettled(progress)) return "ready";

  /* A file still queued or uploading, or accepted before the server lists it, is worth waiting on. */
  const inFlight = picked.some(entry => {
    const state = states.get(entry.id);
    return state?.kind !== "done" || state.outcome.status !== "rejected";
  });
  if (inFlight) return "reading";
  if (progress === null) return "unknown";
  return progress.documents.length === 0 ? "none" : "reading";
}

/* Contact is lost, but what was already read still counts, so the line keeps the number it had. */
function lostMessage(progress: ProcessingProgress | null): string {
  const found = progress?.markersFound.length ?? 0;
  const kept = found > 0 ? `Found ${String(found)} markers in your labs. ` : "";
  return `${kept}We can't check on your labs right now. Keep this page open. We'll keep trying.`;
}
function activity(sessionFailed: boolean, lost: boolean, picked: readonly Picked[], states: ReadonlyMap<string, FileState>, progress: ProcessingProgress | null): {
  readonly message: string;
  readonly state: StatusState;
} | null {
  if (sessionFailed) return {
    message: "We couldn't start your upload. Check your connection.",
    state: "problem"
  };
  if (lost) return {
    message: lostMessage(progress),
    state: "problem"
  };
  const uploading = picked.find(entry => states.get(entry.id)?.kind === "uploading");
  if (uploading) return {
    message: `Uploading ${uploading.file.name}`,
    state: "pending"
  };
  const queued = picked.find(entry => (states.get(entry.id)?.kind ?? "waiting") === "waiting");
  if (queued) return {
    message: `Queued ${queued.file.name}`,
    state: "pending"
  };
  const busy = progress?.documents.find(document => document.status === "stored" || document.status === "extracting");
  if (busy?.status === "extracting") return {
    message: `Reading ${busy.filename}`,
    state: "pending"
  };
  if (busy?.status === "stored") return {
    message: `Queued ${busy.filename}`,
    state: "pending"
  };
  const found = progress?.markersFound.length ?? 0;
  if (found > 0) return {
    message: `Found ${String(found)} markers in your labs`,
    state: "ok"
  };
  const failed = progress?.documents.find(document => document.status === "failed");
  if (failed) return {
    message: failed.failureReason ?? "We could not read this file.",
    state: "problem"
  };
  if (readingSettled(progress) && found === 0) return {
    message: "We found no markers in your labs",
    state: "problem"
  };
  if (picked.some(entry => accepted(states.get(entry.id)))) return {
    message: "Reading your labs",
    state: "pending"
  };
  return null;
}
export function LabsStep() {
  const {
    picked,
    remove,
    choose,
    takePhoto
  } = useUploads();
  const {
    states,
    sessionFailed,
    retry
  } = useUploadQueue(picked);
  const anyAccepted = picked.some(entry => accepted(states.get(entry.id)));
  const [hasSession, setHasSession] = useState(false);
  const {
    progress,
    lost
  } = useLabReading(anyAccepted || hasSession);
  const empty = picked.length === 0 && (progress?.documents.length ?? 0) === 0;
  const now = activity(sessionFailed, lost, picked, states, progress);
  const readyForQuestions = readingSettled(progress);
  const labs = labsReadiness(picked, states, progress);
  const waitingOnLabs = anyAccepted && !readyForQuestions && (progress?.results.length ?? 0) === 0;
  const showReport = progress !== null && (readyForQuestions || progress.results.length > 0);
  const [answersComplete, setAnswersComplete] = useState(false);
  const [inReview, setInReview] = useState(false);
  const [told, setTold] = useState("");
  const was = useRef(labs);

  /* The wait turns into a button without a word, so a screen reader is told when it does. */
  useEffect(() => {
    if (was.current === "reading" && labs === "ready") setTold("Your labs are ready.");
    was.current = labs;
  }, [labs]);
  useEffect(() => {
    void signedIn().then(setHasSession);
  }, []);
  useEffect(() => {
    if (!anyAccepted && !hasSession) return;
    void Promise.all([loadIntake(), loadCurrentFormula()]).then(([loaded, formula]) => {
      setAnswersComplete(loaded !== null && intakeComplete(loaded));
      setInReview(formula !== null);
    });
  }, [anyAccepted, hasSession, picked]);
  return <Shell title={empty ? "Upload your labs" : "Your labs"} step={1} of={3} aside={<>
          {empty ? <PouchShot placement="column" caption="An example. Yours is built from your labs." /> : null}
          <WhatHappens priceBesideForm />
        </>}>
      <div {...stylex.props(stepText.block)}>
        <h1 {...stylex.props(stepText.heading)}>{empty ? "Upload your labs" : "Your labs"}</h1>
        <p {...stylex.props(stepText.lead)}>
          Add every report from your latest blood draw. PDF, photo or screenshot, from Function,
          Superpower, Rythm, Quest, Labcorp or any other lab.
        </p>
      </div>
      {(now || waitingOnLabs) && <Status state={now?.state ?? "pending"} testId="lab-activity">
          {now?.message ?? "Reading your labs"}
        </Status>}
      {picked.length > 0 && <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" aria-label="Your lab reports">
          {picked.map(entry => <FileRow key={entry.id} entry={entry} state={states.get(entry.id)} document={documentFor(states.get(entry.id), progress)} onRetry={() => retry(entry)} onRemove={() => remove(entry.id)} />)}
        </ul>}
      <div className="x78zum5 x1q0g3np xz35z2r x1a02dak x6s0dn4 x1vnjz6c x626il7">
        <Button testId="add-files" tone={empty ? "solid" : "quiet"} onClick={choose}>
          {empty ? "Upload your labs" : "Add more files"}
        </Button>
        <span className="x1s85apg xjb4iqp">
          <Button tone="quiet" onClick={takePhoto}>
            Take a photo
          </Button>
        </span>
        {empty && <span className="x1s85apg xwqx9jr xl4dl4e x1jty25b">Or drop them anywhere.</span>}
      </div>
      {waitingOnLabs && <div className="x78zum5 xdt5ytf x626il7" data-testid="findings-pending">
          <Skeleton bone="row" />
          <Skeleton bone="row" />
          <Skeleton bone="row" />
        </div>}
      {showReport && progress && <Findings progress={progress} />}
      <span role="status" {...stylex.props(visuallyHidden.hidden)}>
        {told}
      </span>
      {(anyAccepted || hasSession) && <EmailSection progress={{
      labs,
      answersComplete,
      inReview
    }} />}
    </Shell>;
}